import { gql, useMutation } from '@apollo/client';
import { Alert, Box, Stack } from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import { Button } from '../components/coreComponents/button';
import { Input } from '../components/coreComponents/input';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { useIdentity } from '../identityContext';

// Mirrors the main app's /settings/security/password. First-time set hides the
// current-password field (the resolver provisions the credential); the change
// flow requires the current password. Min length 12 matches the citadel rule.
export function AdminSecurityPassword() {
  const identity = useIdentity();
  const [update] = useMutation(updateMutation, { refetchQueries: ['AdminIdentity'] });
  const hasPassword = identity.isPasswordSet;

  const formik = useFormik({
    initialValues: { currentPassword: '', newPassword: '', passwordConfirm: '' },
    validate: values => {
      const errors: Record<string, string> = {};
      if (hasPassword && !values.currentPassword) {
        errors.currentPassword = 'Your current password is required';
      }
      if (!values.newPassword) {
        errors.newPassword = 'A new password is required';
      } else if (values.newPassword.length < 12) {
        errors.newPassword = 'Use at least 12 characters';
      } else if (values.newPassword.length > 128) {
        errors.newPassword = 'Use at most 128 characters';
      }
      if (values.passwordConfirm !== values.newPassword) {
        errors.passwordConfirm = 'Passwords do not match';
      }
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        await update({
          variables: {
            input: {
              ...(hasPassword ? { currentPassword: values.currentPassword } : {}),
              newPassword: values.newPassword
            }
          }
        });
        helpers.resetForm();
        helpers.setStatus({ ok: true });
      } catch (err: any) {
        helpers.setStatus({ error: err.message ?? 'Failed to update password' });
      }
    }
  });

  const status = formik.status as { ok?: boolean; error?: string } | undefined;

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Sign-in & security"
        description={hasPassword ? 'Change your password' : 'Set a password for your account'}
      />

      <Box sx={{ maxWidth: 480 }}>
        <Stack spacing={5}>
          {!hasPassword && (
            <Alert severity="info">
              You don't have a password set yet. You can keep signing in with the one-time email
              code, or set a password here to sign in with it from now on.
            </Alert>
          )}

          {hasPassword && (
            <Input
              label="Current password"
              type="password"
              name="currentPassword"
              value={formik.values.currentPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.currentPassword && formik.errors.currentPassword
                  ? formik.errors.currentPassword
                  : undefined
              }
              disabled={formik.isSubmitting}
            />
          )}

          <Input
            label="New password"
            type="password"
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.newPassword && formik.errors.newPassword
                ? formik.errors.newPassword
                : undefined
            }
            disabled={formik.isSubmitting}
          />

          <Input
            label="Confirm new password"
            type="password"
            name="passwordConfirm"
            value={formik.values.passwordConfirm}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.passwordConfirm && formik.errors.passwordConfirm
                ? formik.errors.passwordConfirm
                : undefined
            }
            disabled={formik.isSubmitting}
          />

          {status?.error != null && <Alert severity="error">{status.error}</Alert>}
          {status?.ok === true && <Alert severity="success">Password updated.</Alert>}

          <Box>
            <Button
              variant="primary"
              onClick={() => formik.handleSubmit()}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </MainWrapper>
  );
}

const updateMutation = gql<AdminPasswordUpdateGQL>`
  mutation AdminPasswordUpdate($input: PasswordUpdateInput!) {
    adminPasswordUpdate(input: $input)
  }
`;
